<template>
  <section class="parity-page">
    <div class="page-header">
      <div>
        <p class="eyebrow">Sizing Verification</p>
        <h2>Random Completed Sizing Parity</h2>
        <p>
          Sample completed ESP onshore sizings and compare direct browser
          calculations against the Falcon sizing calculation service.
        </p>
        <p class="status-note">
          Parity means browser and service metrics match. A calculation can
          still be classified as warning or fail because the sizing itself
          violates deterministic design checks.
        </p>
      </div>
      <Button
        label="Run Parity"
        icon="fas fa-play"
        class="p-button-success"
        :loading="isRunning"
        @click="runParity"
      />
    </div>

    <Card class="settings-card">
      <template #title>Run Settings</template>
      <template #content>
        <div class="settings-grid">
          <label>
            Sample Count
            <InputNumber
              v-model="sampleCount"
              :min="1"
              :max="50"
              show-buttons
            />
          </label>
          <label>
            Scan Multiplier
            <InputNumber
              v-model="scanMultiplier"
              :min="1"
              :max="25"
              show-buttons
            />
          </label>
          <label>
            Minimum Pass %
            <InputNumber
              v-model="minimumPassPercent"
              :min="0"
              :max="100"
              suffix="%"
              show-buttons
            />
          </label>
          <label>
            Completed Dispositions
            <InputText
              v-model="dispositionsText"
              placeholder="accepted,closed"
            />
          </label>
        </div>
        <p class="hint">
          The browser uses your current session token. Results are not
          persisted; copy JSON artifacts from this page when triaging parity
          mismatches.
        </p>
      </template>
    </Card>

    <div v-if="error" class="error-banner">
      {{ error }}
    </div>
    <div v-if="warning" class="warning-banner">
      {{ warning }}
    </div>

    <div class="summary-grid">
      <Card>
        <template #title>Fixtures</template>
        <template #content
          ><span class="metric-number">{{
            summary.fixtureCount
          }}</span></template
        >
      </Card>
      <Card>
        <template #title>Parity Matched</template>
        <template #content>
          <span class="metric-number success">{{
            summary.parityMatchedCount
          }}</span>
        </template>
      </Card>
      <Card>
        <template #title>Mismatches</template>
        <template #content>
          <span
            class="metric-number"
            :class="{ danger: summary.mismatchCount > 0 }"
          >
            {{ summary.mismatchCount }}
          </span>
        </template>
      </Card>
      <Card>
        <template #title>Calculation Statuses</template>
        <template #content>
          <div class="status-counts">
            <Tag value="pass" severity="success" />
            {{ summary.statusCounts.pass || 0 }}
            <Tag value="warning" severity="warning" />
            {{ summary.statusCounts.warning || 0 }}
            <Tag value="fail" severity="danger" />
            {{ summary.statusCounts.fail || 0 }}
          </div>
        </template>
      </Card>
      <Card>
        <template #title>Generated</template>
        <template #content>{{ summary.generatedAt || "Not run yet" }}</template>
      </Card>
    </div>

    <DataTable
      v-model:expandedRows="expandedRows"
      :value="results"
      data-key="fixtureId"
      responsive-layout="scroll"
    >
      <template #empty>
        <p class="text-center">Run parity to see sampled sizing results.</p>
      </template>
      <Column expander header-style="width: 3rem" />
      <Column field="sizingTitle" header="Sizing" />
      <Column field="scenarioTitle" header="Scenario" />
      <Column header="Parity">
        <template #body="{ data }">
          <Tag
            :value="data.mismatchCount ? 'Drift' : 'Matched'"
            :severity="data.mismatchCount ? 'danger' : 'success'"
          />
        </template>
      </Column>
      <Column field="status" header="Calculation Status">
        <template #body="{ data }">
          <Tag :value="data.status" :severity="statusSeverity(data.status)" />
        </template>
      </Column>
      <Column field="traceId" header="Trace ID">
        <template #body="{ data }">
          <div class="trace-cell">
            <code>{{ data.traceId }}</code>
            <Tag
              v-if="!data.traceVerified"
              value="Trace missing"
              severity="danger"
            />
          </div>
        </template>
      </Column>
      <Column field="mismatchCount" header="Mismatches">
        <template #body="{ data }">
          <Tag
            :value="String(data.mismatchCount)"
            :severity="data.mismatchCount ? 'danger' : 'success'"
          />
        </template>
      </Column>
      <Column header="Links">
        <template #body="{ data }">
          <RouterLink :to="`/sizings/${data.sizingId}/reports`"
            >Report</RouterLink
          >
        </template>
      </Column>
      <template #expansion="{ data }">
        <div class="result-detail">
          <div class="detail-header">
            <div>
              <h3>{{ data.sizingTitle }}</h3>
              <p>{{ data.scenarioTitle }} · Trace {{ data.traceId }}</p>
              <p class="engine-provenance">
                Engine {{ data.engineVersion }} · Hash
                <code>{{ data.engineHash }}</code>
                <span v-if="data.elapsedMs !== null">
                  · {{ data.elapsedMs }} ms</span
                >
                · Trace
                {{ data.traceVerified ? "verified" : "NOT retrievable" }}
              </p>
            </div>
            <div class="detail-actions">
              <Button
                label="Copy Metrics JSON"
                class="p-button-text"
                @click="copyJson(metricRows(data))"
              />
              <Button
                label="Copy Trace ID"
                class="p-button-text"
                @click="copyText(data.traceId)"
              />
            </div>
          </div>

          <DataTable
            :value="metricRows(data)"
            responsive-layout="scroll"
            size="small"
          >
            <Column field="metricName" header="Metric" />
            <Column field="unit" header="Unit" />
            <Column field="browserValue" header="Browser" />
            <Column field="serviceValue" header="Service" />
            <Column field="difference" header="Diff" />
            <Column field="tolerance" header="Tolerance" />
            <Column header="Parity">
              <template #body="{ data: row }">
                <Tag
                  :value="row.matched ? 'Matched' : 'Drift'"
                  :severity="row.matched ? 'success' : 'danger'"
                />
              </template>
            </Column>
          </DataTable>

          <div class="issues-grid mt-3">
            <Panel header="Warnings">
              <p v-if="!data.serviceWarnings.length">None</p>
              <ul v-else>
                <li
                  v-for="(serviceWarning, warningIndex) in data.serviceWarnings"
                  :key="`${serviceWarning.code}-${warningIndex}`"
                >
                  <code>{{ serviceWarning.code }}</code>
                  {{ serviceWarning.message }}
                </li>
              </ul>
            </Panel>
            <Panel header="Hard Failures">
              <p v-if="!data.serviceHardFailures.length">None</p>
              <ul v-else>
                <li
                  v-for="(failure, failureIndex) in data.serviceHardFailures"
                  :key="`${failure.code}-${failureIndex}`"
                >
                  <code>{{ failure.code }}</code> {{ failure.message }}
                </li>
              </ul>
            </Panel>
          </div>
        </div>
      </template>
    </DataTable>

    <Panel v-if="allMismatches.length" header="Mismatch Details" class="mt-4">
      <DataTable :value="allMismatches" responsive-layout="scroll">
        <Column field="fixtureId" header="Fixture" />
        <Column field="metricName" header="Metric" />
        <Column field="frontendValue" header="Browser" />
        <Column field="serviceValue" header="Service" />
        <Column field="difference" header="Diff" />
        <Column field="tolerance" header="Tolerance" />
        <Column field="likelySource" header="Likely Source" />
      </DataTable>
    </Panel>

    <Panel v-if="results.length" header="JSON Artifacts" class="mt-4">
      <div class="artifact-actions">
        <Button
          label="Copy Results JSON"
          class="p-button-text"
          @click="copyJson(results)"
        />
        <Button
          label="Copy Summary JSON"
          class="p-button-text"
          @click="copyJson(summary)"
        />
      </div>
      <AdminJsonViewer :value="{ summary, results }" />
    </Panel>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import { useToast } from "primevue/usetoast";

import {
  discoverRandomCompletedSizings,
  runSizingParityForCandidate,
  summarizeAdminParity,
  type AdminParityResult,
  type AdminParitySummary
} from "@/libs/aiDos/adminParity";
import {
  copyJsonToClipboard,
  copyTextToClipboard,
  errorMessage,
  statusSeverity
} from "@/libs/aiDos/adminShared";
import {
  compareParityMetricValues,
  SIZING_PARITY_METRICS
} from "@/serverSizing/parityMetrics";
import AdminJsonViewer from "./AdminJsonViewer.vue";

const toast = useToast();
const isRunning = ref(false);
const error = ref<string | null>(null);
const warning = ref<string | null>(null);
const sampleCount = ref(10);
const scanMultiplier = ref(5);
const minimumPassPercent = ref(50);
const dispositionsText = ref("accepted,closed");
const results = ref<AdminParityResult[]>([]);
const expandedRows = ref({});
const summary = ref<AdminParitySummary>({
  generatedAt: "",
  fixtureCount: 0,
  mismatchCount: 0,
  parityMatchedCount: 0,
  statusCounts: {},
  byMetric: {},
  byLikelySource: {}
});

const dispositions = computed(() =>
  dispositionsText.value
    .split(",")
    .map(value => value.trim())
    .filter(Boolean)
);
const allMismatches = computed(() =>
  results.value.flatMap(result => result.mismatches)
);

const runParity = async () => {
  isRunning.value = true;
  error.value = null;
  warning.value = null;
  results.value = [];
  try {
    const candidates = await discoverRandomCompletedSizings({
      count: (sampleCount.value || 10) * (scanMultiplier.value || 5),
      dispositions: dispositions.value
    });
    const desiredCount = sampleCount.value || 10;
    const requiredPassCount = Math.min(
      desiredCount,
      Math.ceil(desiredCount * ((minimumPassPercent.value || 0) / 100))
    );
    const maxFillerCount = desiredCount - requiredPassCount;
    const passResults: AdminParityResult[] = [];
    const fillerResults: AdminParityResult[] = [];
    const candidateFailures: string[] = [];
    let scannedCount = 0;
    for (const candidate of candidates) {
      // Keep scanning past the first desiredCount candidates until the pass
      // coverage target is met or the discovered pool is exhausted.
      if (
        passResults.length >= requiredPassCount &&
        passResults.length + fillerResults.length >= desiredCount
      ) {
        break;
      }
      scannedCount += 1;
      let result: AdminParityResult;
      try {
        result = await runSizingParityForCandidate(
          candidate,
          passResults.length + fillerResults.length
        );
      } catch (err) {
        // One failing sizing must not abort the whole parity run.
        candidateFailures.push(`${candidate.id}: ${errorMessage(err)}`);
        continue;
      }
      if (result.status === "pass" && passResults.length < desiredCount) {
        passResults.push(result);
      } else if (
        result.status !== "pass" &&
        fillerResults.length < maxFillerCount
      ) {
        fillerResults.push(result);
      }
      results.value = [...passResults, ...fillerResults].slice(0, desiredCount);
    }
    const parityResults = [...passResults, ...fillerResults].slice(
      0,
      desiredCount
    );
    results.value = parityResults;
    summary.value = summarizeAdminParity(parityResults);
    const warningParts: string[] = [];
    if (passResults.length < requiredPassCount) {
      warningParts.push(
        `Scanned ${scannedCount} completed sizings but found only ${passResults.length} passing calculation statuses. Target was ${requiredPassCount} for ${minimumPassPercent.value}% pass coverage.`
      );
    }
    if (candidateFailures.length) {
      warningParts.push(
        `${candidateFailures.length} sizing(s) failed to execute and were skipped: ${candidateFailures.join("; ")}`
      );
    }
    warning.value = warningParts.length ? warningParts.join(" ") : null;
    if (parityResults.length === 0) {
      throw new Error(
        candidateFailures.length
          ? "No completed sizing candidates completed parity execution."
          : "No completed sizing candidates were discovered."
      );
    }
    toast.add({
      severity:
        parityResults.length < (sampleCount.value || 10) ||
        summary.value.mismatchCount
          ? "warn"
          : "success",
      summary: "Sizing parity complete",
      detail: `${summary.value.fixtureCount} fixtures from ${scannedCount} scanned, ${summary.value.mismatchCount} mismatches`,
      life: 5000
    });
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    isRunning.value = false;
  }
};

const copyJson = (value: unknown) => copyJsonToClipboard(toast, value);

const copyText = (value: string) => copyTextToClipboard(toast, value);

const metricRows = (result: AdminParityResult) =>
  SIZING_PARITY_METRICS.map(definition => {
    const browserMetric = result.browserMetrics[definition.name];
    const serviceMetric = result.serviceMetrics[definition.name];
    const comparison = compareParityMetricValues(
      definition,
      browserMetric,
      serviceMetric
    );
    return {
      metricName: definition.name,
      unit: comparison.unit,
      browserValue: comparison.frontendValue,
      serviceValue: comparison.serviceValue,
      difference: comparison.difference,
      tolerance: definition.tolerance,
      matched: comparison.matched
    };
  });
</script>

<style scoped lang="scss">
.page-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
    max-width: 56rem;
  }
}

.status-note {
  margin-top: 0.75rem;
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.settings-card,
.summary-grid {
  margin-bottom: 1rem;
}

.settings-grid,
.summary-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 700;
}

.hint {
  color: var(--text-color-secondary);
  margin-bottom: 0;
}

.metric-number {
  font-size: 2rem;
  font-weight: 800;

  &.success {
    color: var(--green-500);
  }

  &.danger {
    color: var(--red-500);
  }
}

.status-counts {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.5rem;
  color: var(--red-700);
  margin-bottom: 1rem;
  padding: 1rem;
}

.warning-banner {
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 0.5rem;
  color: var(--yellow-700);
  margin-bottom: 1rem;
  padding: 1rem;
}

.artifact-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.result-detail {
  background: var(--surface-50);
  border-radius: 0.75rem;
  padding: 1rem;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  h3 {
    margin: 0 0 0.25rem;
  }

  p {
    color: var(--text-color-secondary);
    margin: 0;
  }
}

.detail-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.trace-cell {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.engine-provenance {
  font-size: 0.82rem;
  margin-top: 0.25rem;

  code {
    word-break: break-all;
  }
}

.issues-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
}

.json-artifact-output {
  background: #111827;
  border-radius: 0.5rem;
  border: 1px solid #374151;
  color: #f9fafb;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.45;
  max-height: 28rem;
  overflow: auto;
  padding: 1rem;
  text-shadow: none;
  white-space: pre;
}
</style>
